import { refreshSession } from "@/lib/api";
import { useCallback, useEffect, useRef, useState } from "react";
import { ACTIVITY_EVENTS, IDLE_TIMEOUT_MS, IDLE_WARNING_MS } from "./session-policy";
import { useAuthStore } from "./store";

const PRE_WARNING_MS = IDLE_TIMEOUT_MS - IDLE_WARNING_MS;
// Reschedule the idle timer at most this often, so a stream of mousemove events
// costs one clearTimeout/setTimeout per second instead of hundreds.
const ACTIVITY_THROTTLE_MS = 1000;

export interface IdleLogout {
  /** True while the "you're about to be logged out" countdown modal is shown. */
  warningActive: boolean;
  /** Milliseconds left in the countdown (only meaningful while warningActive). */
  remainingMs: number;
  /** "Stay signed in" — proactively refresh the session and dismiss the warning. */
  stayActive: () => Promise<void>;
  /** "Log out now" — end the session immediately (manual logout). */
  logoutNow: () => void;
}

interface Options {
  /** Injectable for tests; defaults to the shared single-flight refresh. */
  onRefresh?: () => Promise<boolean>;
}

/**
 * IR-1241 — inactivity auto-logout. After IDLE_TIMEOUT_MS of no operator
 * activity, surfaces a countdown warning for the final IDLE_WARNING_MS; if the
 * operator does not act, performs a full idle logout. Mount this ONLY inside an
 * authenticated subtree (the /_authed layout) so the timers never run on the
 * login screen.
 */
export function useIdleLogout(options: Options = {}): IdleLogout {
  const onRefresh = options.onRefresh ?? refreshSession;

  const [warningActive, setWarningActive] = useState(false);
  const [remainingMs, setRemainingMs] = useState(IDLE_WARNING_MS);

  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityAt = useRef(0);
  // Ref mirror of warningActive so the (stable) activity listener reads the
  // latest value without being re-bound on every state change.
  const warningRef = useRef(false);
  warningRef.current = warningActive;

  const clearTimers = useCallback(() => {
    if (warnTimer.current) clearTimeout(warnTimer.current);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    if (countdownTimer.current) clearInterval(countdownTimer.current);
    warnTimer.current = null;
    logoutTimer.current = null;
    countdownTimer.current = null;
  }, []);

  const beginWarning = useCallback(() => {
    setWarningActive(true);
    setRemainingMs(IDLE_WARNING_MS);
    const deadline = Date.now() + IDLE_WARNING_MS;
    countdownTimer.current = setInterval(() => {
      setRemainingMs(Math.max(0, deadline - Date.now()));
    }, 1000);
    logoutTimer.current = setTimeout(() => {
      clearTimers();
      useAuthStore.getState().logout("idle");
    }, IDLE_WARNING_MS);
  }, [clearTimers]);

  const scheduleIdle = useCallback(() => {
    clearTimers();
    setWarningActive(false);
    warnTimer.current = setTimeout(beginWarning, PRE_WARNING_MS);
  }, [beginWarning, clearTimers]);

  const stayActive = useCallback(async () => {
    const ok = await onRefresh();
    if (ok) {
      scheduleIdle();
    } else {
      clearTimers();
      useAuthStore.getState().logout("expired");
    }
  }, [onRefresh, scheduleIdle, clearTimers]);

  const logoutNow = useCallback(() => {
    clearTimers();
    useAuthStore.getState().logout("manual");
  }, [clearTimers]);

  useEffect(() => {
    scheduleIdle();

    const onActivity = () => {
      // Once the warning is up the operator must click a button — passive
      // activity (a stray mousemove) must not silently cancel the logout.
      if (warningRef.current) return;
      const now = Date.now();
      if (now - lastActivityAt.current < ACTIVITY_THROTTLE_MS) return;
      lastActivityAt.current = now;
      scheduleIdle();
    };

    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true });
    }
    return () => {
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, onActivity);
      }
      clearTimers();
    };
  }, [scheduleIdle, clearTimers]);

  return { warningActive, remainingMs, stayActive, logoutNow };
}
